import { useState } from 'react'
import { Feed } from './Feed'
import { Quiz } from './Quiz'
import { LectureDunTag } from './LectureDunTag'
import { PortesDuTag } from './PortesDuTag'
import { ObjectifFlottant } from './ObjectifFlottant'
import { Tabs } from '@/components/ui/Tabs'
import { useQuery } from '@tanstack/react-query'
import * as progression from '@/lib/api/progression'
import type { ModeDuFil } from '@/lib/api/feed'

/**
 * L'écran du Fil et ses trois listes — 02 §F2.2 à §F2.4.
 *
 * **Ce sont trois périmètres, pas trois écrans.** « L'onglet Marque-pages
 * présente ces items COMME UN FIL : même carte, même interaction, même
 * défilement. Ce n'est pas une liste de liens » (RG-2.20). Une liste de titres
 * à ouvrir un par un ferait perdre le seul geste que le candidat a appris —
 * défiler et répondre — et personne n'y reviendrait.
 *
 * Répondre dans les marque-pages compte normalement : XP, répétition espacée,
 * maîtrise (RG-2.22). Il n'y a rien à faire pour cela, et c'est le signe que
 * le découpage est le bon : le journal est unique (D-15).
 */
const LISTES: { id: ModeDuFil; label: string }[] = [
  { id: 'feed', label: 'Fil' },
  { id: 'bookmarks', label: 'Marque-pages' },
  { id: 'review', label: 'À revoir' },
]

/**
 * La pastille d'objectif, alimentée par la progression du candidat.
 *
 * Séparée du reste pour que l'attente de la requête n'ajoute rien au premier
 * rendu du Fil : tant qu'elle n'a pas répondu, la pastille n'existe pas, et
 * les cartes s'affichent sans l'attendre.
 */
function ObjectifDuJour() {
  const { data } = useQuery({
    queryKey: ['progression'],
    queryFn: progression.progression,
    // Le Fil invalide cette clé après chaque salve : inutile de sonder.
    staleTime: 60_000,
  })

  if (data === undefined) return null

  const { objectif, effort } = data.data

  // La série descend avec l'objectif : la pastille ne l'affiche qu'une fois la
  // cible franchie, mais c'est elle qui décide — §83.
  return (
    <ObjectifFlottant
      fait={objectif.xp_du_jour}
      objectif={objectif.cible_xp}
      serie={effort.serie}
    />
  )
}

export function FeedScreen({
  economieDeDonnees = false,
  onOuvrirOnglet,
}: {
  economieDeDonnees?: boolean
  /** Où mène la carte de découverte — F22 §7, [DECISIONS §366]. */
  onOuvrirOnglet?: ((onglet: string) => void) | undefined
}) {
  const [mode, setMode] = useState<ModeDuFil>('feed')

  // Le quiz REMPLACE le fil au lieu de s'ouvrir à côté : il est chronométré, et
  // laisser une barre d'onglets sous une question qui tourne invite à en
  // sortir. Ce n'est pas ce qu'on entraîne.
  const [quiz, setQuiz] = useState<{ path: string; label: string } | null>(null)

  // RG-2.38 — le tag tapé, en attente du choix de porte. Distinct de `quiz` :
  // c'est le tag de la CARTE, alors que `quiz` porte le thème remonté, qui est
  // presque toujours un cran plus haut (RG-2.25).
  const [tagTape, setTagTape] = useState<
    { path: string; label: string; item?: string } | null
  >(null)

  // La lecture ouverte depuis un tag. Elle remplace le Fil comme le quiz, et
  // pour la même raison : lire une leçon dans une colonne qui défile encore
  // sous elle ne se fait pas.
  const [lecture, setLecture] = useState<
    { path: string; label: string; item?: string } | null
  >(null)

  if (quiz !== null) {
    return (
      <Quiz
        tag={quiz}
        economieDeDonnees={economieDeDonnees}
        onQuitter={() => {
          setQuiz(null)
        }}
      />
    )
  }

  if (lecture !== null) {
    return (
      <LectureDunTag
        tag={lecture}
        economieDeDonnees={economieDeDonnees}
        onQuitter={() => {
          setLecture(null)
        }}
      />
    )
  }

  return (
    <div>
      <Tabs
        tabs={LISTES}
        active={mode}
        onChange={(id) => {
          setMode(id as ModeDuFil)
        }}
      />

      {/* La clé remonte le composant à chaque changement de liste : les
          réponses en cours, les dévoilements et les marques appartiennent à
          la liste où ils ont eu lieu. Les laisser filtrer d'un onglet à
          l'autre afficherait une correction sur une carte qui n'est pas la
          bonne. */}
      <Feed
        key={mode}
        mode={mode}
        economieDeDonnees={economieDeDonnees}
        onOuvrirOnglet={onOuvrirOnglet}
        onQuiz={(tag) => {
          setTagTape(tag)
        }}
      />

      {/* RG-2.41 — l'objectif FLOTTE, et seulement dans le Fil. Une barre en
          haut de page n'existe plus au bout de trois cartes ; or l'objectif ne
          sert qu'au moment où l'on décide de continuer ou d'arrêter, et ce
          moment arrive en plein défilement.

          La pastille se pose d'elle-même au coin bas-droit de l'application,
          à la place du bouton flottant de Twitter : sa position ne dépend plus
          de l'endroit où elle est déclarée.

          Il n'apparaît pas dans les marque-pages ni dans « à revoir » : ces
          réponses ne le font pas avancer, et l'y montrer figé donnerait
          l'impression d'un compteur cassé. */}
      {mode === 'feed' && <ObjectifDuJour />}

      {/* RG-2.38 — deux portes, et on demande laquelle. Le tag ne mène plus
          directement nulle part : la moitié des tapes tombaient au mauvais
          endroit, dans un sens comme dans l'autre. */}
      {tagTape !== null && (
        <PortesDuTag
          tag={tagTape}
          onQuiz={(theme) => {
            setTagTape(null)
            setQuiz(theme)
          }}
          onLecon={() => {
            setLecture(tagTape)
            setTagTape(null)
          }}
          onFerme={() => {
            setTagTape(null)
          }}
        />
      )}
    </div>
  )
}
